/**
 * 데이터 시각화(데이터 연관관계·지식그래프) 데이터 공급 모듈 — M3D 허브판. 라우트 /data-visualization*
 * 개발/운영 DB 의 실험입력 데이터를 기존 조회 API(전부 GET)로 읽어 분석용 구조로 가공한다.
 *
 * 계열 = 공정도 1건. 환경별로 공정도 id 가 다를 수 있어 공정도 "이름 포함 매칭"으로 런타임에
 * 해석한다 (Hub_Workspace/docs/data-visualization-plan-m3d.md). 상위항목 = 공정 노드(dictionary key link, 이름은 사전
 * 대표태그명), 하위항목 = 노드별 속성.
 *
 * 계열 선정(2026-10-19, dev DB 기준 분석 가능 숫자 속성 수): n형 반도체 48 / p형 반도체 59 /
 * 적층소재 27 / M3D 소자 22 / 계산 데이터 16. 「전구체」(3개)·「문헌 데이터」(행 2건)는 제외.
 */

export const SERIES = [
  { key: "NMOS", name: "n형 반도체", match: "n형 반도체" },
  { key: "PMOS", name: "p형 반도체", match: "p형 반도체" },
  { key: "STK", name: "적층소재", match: "적층소재" },
  { key: "M3D", name: "M3D 소자", match: "M3D 소자" },
  { key: "CAL", name: "계산 데이터", match: "계산 데이터" },
];

// 유효 숫자값이 이 수 미만인 속성은 숨긴다 (결정 D-AD6)
export const MIN_VALID = 3;

const cache = {
  seriesProcess: null, // key → { processId, processName } | null
  loaded: {},          // key → loadSeries 결과
};

function stripTags(s) {
  return String(s || "").replace(/<[^>]*>/g, "").trim();
}

/** 공정도 목록에서 3계열의 processId 를 해석한다. 결과는 캐시. */
export async function resolveSeries(wApi) {
  if (cache.seriesProcess) return cache.seriesProcess;
  const r = await wApi.process.search({
    query: "", pageNo: 0, perPage: 100,
    isThumbnail: 0, isProtocol: 0, isMyData: 0, processType: "NORMAL",
  });
  const list = (r && r.content) || [];
  const out = {};
  for (const s of SERIES) {
    const hit = list.find((p) => stripTags(p.processName).includes(s.match));
    out[s.key] = hit
      ? { processId: hit.processId, processName: stripTags(hit.processName) }
      : null;
  }
  cache.seriesProcess = out;
  return out;
}

/**
 * 한 계열의 분석 데이터를 로딩한다. 반환:
 * {
 *   series, processId, processName,
 *   groups: [{ id, name, tagType, props: [prop] }],   // 상위항목(값 있는 속성이 있는 그룹만)
 *   numericProps: [prop],                              // 분석 가능(숫자·유효 n≥MIN_VALID·분산>0)
 *   rows: [{ id, order, label }],                      // 실험 행(rowOrder 순). label = 샘플명(자리표시 값이면 "행 N")
 *   rowCount,
 * }
 * prop = { id, name, unit, type, groupId, groupName, values: {rowId: number}, validCount, mean, std, active }
 */
export async function loadSeries(wApi, seriesKey) {
  if (cache.loaded[seriesKey]) return cache.loaded[seriesKey];
  const series = SERIES.find((s) => s.key === seriesKey);
  const procMap = await resolveSeries(wApi);
  const proc = procMap[seriesKey];
  if (!series || !proc) return null;
  const { processId, processName } = proc;

  // 속성 정의는 공정도 속성 직접 조회(get-all-process-property)를 쓴다.
  // KESH 원본의 get-all-experiment-value 는 release_process 가 없는 공정도(미공개)에서 서버 NPE(400)가 나는데,
  // dev 의 M3D 공정도 7건 중 5건이 그 상태였다(2026-10-19 확인). 응답 필드(processPropertyId·propertyName·
  // propertyUnit·propertyType·dictionaryKeyLinkId)는 동일하다.
  // 세 조회를 동시에 시작하고, 노드 이름 조회(링크 수만큼)는 링크 응답이 오는 즉시 시작해
  // 가장 느린 실험값 응답(수 MB)과 겹치게 한다. (직렬일 때 +0.25~0.5초)
  const propDefsP = wApi.property.getAllProcessPropertyList({ processId });
  const linksP = wApi.dictionary.getDictionaryKeyLinksByProcessId({ processId });
  const cellsP = wApi.experiment.getExperimentData({ processId });

  // 노드(상위항목) 이름 해석 — 링크별 사전 대표태그명. 실패해도 그룹핑은 유지.
  const linkName = {};
  const linkType = {};
  const namesP = linksP.then((links) => Promise.all(
    (links || []).map(async (l) => {
      try {
        const d = await wApi.dictionary.getDictionaryByKey({
          key: l.tagKey, tagId: l.tagId, processId,
        });
        linkName[l.id] = (d && d.representativeTagName) || "노드 " + l.id;
        linkType[l.id] = (d && d.tagType) || l.tagType || null;
      } catch (e) {
        linkName[l.id] = "노드 " + l.id;
        linkType[l.id] = l.tagType || null;
      }
    })
  ));
  const [propDefs, links, cells] = await Promise.all([propDefsP, linksP, cellsP]);
  await namesP;

  // 속성 정의: processPropertyId 로 dedupe, 비활성(active=0) 속성 제외
  const propById = {};
  for (const p of propDefs || []) {
    if (p.active === 0 || p.active === false) continue;
    if (!propById[p.processPropertyId]) {
      propById[p.processPropertyId] = {
        id: p.processPropertyId,
        name: p.propertyName,
        unit: p.propertyUnit || "",
        type: p.propertyType,
        classId: p.propertyClassId || null, // property_class: 1 Performance, 2 Process, 3 Property, 4 Structure, 5 metadata, 6 chemical, 7 crystallography
        groupId: p.dictionaryKeyLinkId || 0, // 0 = 기본 정보(노드 미연결)
      };
    }
  }

  // 값: rowId × processPropertyId → 숫자/문자. 행 순서(rowOrder)도 기억한다.
  const rowSet = new Set();
  const rowOrder = {};
  for (const c of cells || []) {
    rowSet.add(c.rowId);
    if (rowOrder[c.rowId] === undefined && c.rowOrder != null) rowOrder[c.rowId] = c.rowOrder;
    const p = propById[c.processPropertyId];
    if (!p) continue;
    const raw = c.value == null ? "" : String(c.value).trim();
    if (raw === "") continue;
    if (!p.values) p.values = {};
    if (!p.texts) p.texts = {};
    p.texts[c.rowId] = raw;
    if (p.type === "NUMBER" || p.type === "NUMBER_ARRAY") {
      const num = parseFloat(raw.replace(/,/g, ""));
      if (!isNaN(num) && isFinite(num)) p.values[c.rowId] = num;
    }
  }

  // 통계·활성 판정
  const props = Object.values(propById);
  for (const p of props) {
    const vals = Object.values(p.values || {});
    p.validCount = vals.length;
    p.textCount = Object.keys(p.texts || {}).length;
    if (vals.length) {
      const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
      const varSum = vals.reduce((a, b) => a + (b - mean) * (b - mean), 0);
      p.mean = mean;
      p.std = Math.sqrt(varSum / vals.length);
    } else {
      p.mean = 0; p.std = 0;
    }
    // 분석 가능: 숫자형 + 유효 n 기준 + 분산 존재 (결정 D-AD6)
    p.active = p.type === "NUMBER" && p.validCount >= MIN_VALID && p.std > 0;
    p.groupName = p.groupId ? (linkName[p.groupId] || "노드 " + p.groupId) : "기본 정보";
  }

  // 그룹(상위항목): 값이 하나라도 있는 속성이 있는 그룹만 노출 (요구 ③)
  const groupMap = {};
  for (const p of props) {
    if (!p.textCount && !p.validCount) continue; // 내용 없는 하위항목 숨김
    if (!groupMap[p.groupId]) {
      groupMap[p.groupId] = {
        id: p.groupId,
        name: p.groupName,
        tagType: p.groupId ? linkType[p.groupId] : null,
        order: null, // 아래에서 nodeOrder 로 채움(공정 순서)
        props: [],
      };
    }
    groupMap[p.groupId].props.push(p);
  }
  const linkOrder = {};
  (links || []).forEach((l, i) => { linkOrder[l.id] = l.nodeOrder != null ? l.nodeOrder : i; });
  Object.values(groupMap).forEach((g) => { g.order = g.id ? (linkOrder[g.id] != null ? linkOrder[g.id] : null) : null; });
  const groups = Object.values(groupMap).sort((a, b) => {
    if (a.id === 0) return -1;
    if (b.id === 0) return 1;
    return (linkOrder[a.id] || 0) - (linkOrder[b.id] || 0);
  });
  groups.forEach((g) => g.props.sort((a, b) => b.validCount - a.validCount));

  // 행 목록: rowOrder 순. 라벨은 「샘플명」 속성 값(자리표시 값이면 "행 N")
  const nameProp = props.find((p) => p.name === "샘플명" && p.texts) || null;
  const placeholder = /^(ex\)|예\)|해당 없음$|example$|-$|sample$)/i;
  const rows = [...rowSet]
    .map((id) => ({ id, order: rowOrder[id] != null ? rowOrder[id] : Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.order - b.order)
    .map((r, i) => {
      const nm = nameProp ? nameProp.texts[r.id] : undefined;
      const label = nm && !placeholder.test(nm.trim()) ? nm.trim() : `행 ${r.order !== Number.MAX_SAFE_INTEGER ? r.order : i + 1}`;
      return { ...r, label };
    });

  // 성능: 결과를 동결해 Vue 반응형 관찰에서 제외한다. 셀 수십만 개의 values/texts 맵이 반응형이 되면
  // 관찰자 등록·의존성 추적만으로 수 초~수십 초가 걸린다(2026-10-20 측정: 필터 1회 23초 → 수 ms).
  // 최상위만 동결해도 Vue 2 는 비확장 객체를 관찰하지 않으므로 하위까지 관찰되지 않는다. 화면은 이 객체를 읽기만 한다.
  const result = Object.freeze({
    series: series.key,
    seriesName: series.name,
    processId,
    processName,
    groups,
    numericProps: props.filter((p) => p.active),
    rows,
    rowCount: rowSet.size,
  });
  cache.loaded[seriesKey] = result;
  return result;
}

/** 두 속성의 피어슨 상관계수 (pairwise-complete). {r, n} */
export function pearson(pa, pb) {
  const xs = [];
  const ys = [];
  const va = pa.values || {};
  const vb = pb.values || {};
  for (const rowId of Object.keys(va)) {
    if (vb[rowId] !== undefined) { xs.push(va[rowId]); ys.push(vb[rowId]); }
  }
  const n = xs.length;
  if (n < MIN_VALID) return { r: null, n };
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, dx = 0, dy = 0;
  for (let i = 0; i < n; i++) {
    const a = xs[i] - mx, b = ys[i] - my;
    num += a * b; dx += a * a; dy += b * b;
  }
  const den = Math.sqrt(dx * dy);
  return { r: den === 0 ? 0 : num / den, n };
}

/** 산점도용 공통 행 좌표 [{x,y}] */
export function pairPoints(px, py) {
  const out = [];
  const vx = px.values || {};
  const vy = py.values || {};
  for (const rowId of Object.keys(vx)) {
    if (vy[rowId] !== undefined) out.push({ x: vx[rowId], y: vy[rowId] });
  }
  return out;
}
